'use client'

import {
  getRemainingSize,
  sumRecordFileSizes,
  toAttachedFile,
  type FilesRecord,
} from '@/lib/utils/upload'
import { atom, useAtom, type PrimitiveAtom } from 'jotai'
import { useEffect, useMemo } from 'react'

type UseDropZoneSharedLimitParams = {
  files: File[]
  filesAtom?: PrimitiveAtom<FilesRecord>
  /** Size budget shared across every field pointing at the same atom, in Mb */
  sharedLimit?: number
  name: string
}

// Publishes this field's files into the shared atom and derives what is left of the common
// budget. Store-only: the form layer stays in DropZoneInput
export function useDropZoneSharedLimit({
  files,
  filesAtom,
  sharedLimit,
  name,
}: UseDropZoneSharedLimitParams) {
  const fallbackAtom = useMemo(() => atom<FilesRecord>({}), [])
  const [sharedFiles, setSharedFiles] = useAtom<FilesRecord>(filesAtom ?? fallbackAtom)

  const isShared = filesAtom !== undefined && sharedLimit !== undefined

  useEffect(() => {
    if (!isShared) return

    setSharedFiles((previous) => {
      // Rebuilds only this field's own keys; other fields' entries must survive untouched,
      // otherwise a field with no files of its own would wipe out the shared budget on every render
      const ownPrefix = `${name}-`
      const otherEntries = Object.fromEntries(
        Object.entries(previous).filter(([key]) => !key.startsWith(ownPrefix)),
      )

      const ownEntries: FilesRecord = {}

      for (const file of files) {
        const key = `${ownPrefix}${file.name}`
        ownEntries[key] = previous[key] ?? [toAttachedFile(file)]
      }

      const next = { ...otherEntries, ...ownEntries }
      const previousKeys = Object.keys(previous)
      const nextKeys = Object.keys(next)
      const isUnchanged =
        previousKeys.length === nextKeys.length &&
        nextKeys.every((key) => previous[key]?.[0]?.size === next[key]?.[0]?.size)

      return isUnchanged ? previous : next
    })
  }, [files, isShared, name, setSharedFiles])

  return {
    remainingSize: getRemainingSize({
      sharedLimitMb: sharedLimit,
      usedBytes: sumRecordFileSizes(sharedFiles),
    }),
  }
}
